import { AgGridAngular } from '@ag-grid-community/angular';
import { GridApi, GridOptions } from '@ag-grid-community/core';
import { ColDef } from '@ag-grid-community/core/dist/esm/es6/entities/colDef';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  input,
  OnInit,
  Output,
  signal,
  untracked,
} from '@angular/core';
import dayjs from 'dayjs';
import { uniq } from 'lodash-es';
import { firstValueFrom } from 'rxjs';

import { InvoiceWorkflowDirective } from '@features/feature-workflow/directives/invoice-workflow.directive';

import { AgRawTooltip } from '@shared/components/ag-raw-tooltip/ag-raw-tooltip';
import { ButtonComponent } from '@shared/components/button/button.component';
import { ErrorsTooltipComponent } from '@shared/components/errors-tooltip/errors-tooltip.component';
import { TableSkeletonComponent } from '@shared/components/table-skeleton/table-skeleton.component';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { TableConstants } from '@shared/constants/table.constants';
import { StickyGridDirective } from '@shared/directives/sticky-grid/sticky-grid.directive';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import {
  EntityType,
  EventType,
  GqlService,
  PermissionType,
  WorkflowStep,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthService } from '@shared/store/auth/auth.service';
import { EventService } from '@shared/store/event/event.service';
import { InvoiceLineItem, InvoiceModel } from '@shared/store/invoice/invoice.model';
import { MainQuery } from '@shared/store/main/main.query';
import { TrialsQuery } from '@shared/store/trials/trials.query';
import { WorkflowQuery } from '@shared/store/workflow/workflow.query';
import { AuxExcelStyles } from '@shared/utils';
import { ExportType, Utils } from '@shared/utils/utils';

import { calculatePinnedBottomData, DefaultColumns } from './invoice-items-integration.constants';
import { MapActivitiesModalService } from '../map-activities/map-activities-modal/map-activities-modal.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [InvoiceWorkflowDirective],
  imports: [
    AgGridAngular,
    ButtonComponent,
    StickyGridDirective,
    TableSkeletonComponent,
    TooltipDirective,
    ErrorsTooltipComponent,
  ],
  selector: 'aux-invoice-items-tab',
  styles: [
    `
      :host {
        @apply my-4 block;
      }
    `,
  ],
  templateUrl: 'invoice-items-integration.component.html',
})
export class InvoiceItemsIntegrationComponent implements OnInit {
  authService = inject(AuthService);
  eventService = inject(EventService);
  gqlService = inject(GqlService);
  invoiceCtx = inject(InvoiceWorkflowDirective);
  mainQuery = inject(MainQuery);
  overlayService = inject(OverlayService);
  trialsQuery = inject(TrialsQuery);
  workflowQuery = inject(WorkflowQuery);

  private mapActivitiesModalService = inject(MapActivitiesModalService);

  gridAPI = signal<GridApi | null>(null);
  gridData = signal<InvoiceLineItem[]>([]);
  loading = signal<boolean>(true);

  mapToBudgetTooltip = computed(() => {
    const errors: string[] = [];

    if (!this.hasEditPermission()) {
      errors.push(MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION);
    }

    if (this.isInvoiceFinalized()) {
      errors.push(this.invoiceLockTooltip());
    }

    if (!this.gridData().length) {
      errors.push(MessagesConstants.INVOICE.NO_LINE_ITEMS_TO_MAP);
    }

    return errors;
  });

  invoice = input.required<InvoiceModel>();
  items = input.required<InvoiceLineItem[]>();

  @Output() changeTab = new EventEmitter<'integration'>();
  columnDefs: ColDef[] = [];
  gridOptions = <GridOptions>{
    defaultColDef: {
      ...TableConstants.DEFAULT_GRID_OPTIONS.DEFAULT_COL_DEF,
    },
    ...TableConstants.DEFAULT_GRID_OPTIONS.GRID_OPTIONS,
    enableRangeSelection: true,
    excelStyles: [...AuxExcelStyles, ...Utils.generateExcelCurrencyStyles(Utils.CURRENCY_OPTIONS)],
    suppressCellFocus: false,
  };
  hasEditPermission = this.authService.$isAuthorized({
    permissions: [PermissionType.PERMISSION_UPDATE_INVOICES],
  });
  invoiceLockTooltip = this.invoiceCtx.invoiceLockTooltip;
  isInvoiceFinalized = this.workflowQuery.getLockStatusByWorkflowStepType(
    WorkflowStep.WF_STEP_MONTH_CLOSE_LOCK_INVOICES
  );
  setBottomData = effect(() => {
    const gridAPI = this.gridAPI();
    const gridData = this.gridData();

    if (!gridAPI) {
      return;
    }

    untracked(() => {
      gridAPI.setGridOption('pinnedBottomRowData', [
        {
          ...calculatePinnedBottomData(gridData),
        },
      ]);
    });
  });

  ngOnInit(): void {
    this.initGrid();
  }

  private getAttributeColumns(attributes: string[]): ColDef[] {
    const columns: ColDef[] = [];

    attributes.forEach((attribute) => {
      columns.push({
        cellClass: '!block text-left max-w whitespace-nowrap overflow-hidden text-ellipsis',
        field: attribute,
        headerName: attribute,
        minWidth: 125,
        resizable: true,
        tooltipComponent: AgRawTooltip,
        tooltipField: attribute,
        valueFormatter: Utils.dashFormatter,
      });
    });

    return columns;
  }

  private initGrid(): void {
    const allAttributes: string[] = [];

    this.gqlService.listInvoiceCostCategoryLineItems$(this.invoice().id).subscribe((response) => {
      if (response.success && response.data?.length) {
        this.mapActivitiesModalService.setLineItemIntegrationOptions(response.data);
        const gridData = response.data.map((item) => {
          const gridRow = {
            amount: item.amount,
            description: item.description,
          };

          item.attributes.forEach((attribute) => {
            allAttributes.push(attribute.attribute_name);
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            gridRow[attribute.attribute_name] = attribute.attribute_value;
          });

          return gridRow;
        });

        this.gridData.set(gridData);
      }

      this.columnDefs = [
        ...DefaultColumns(this.invoice()),
        ...this.getAttributeColumns(uniq(allAttributes)),
      ];

      this.loading.set(false);
    });
  }

  async onExport() {
    const invoice = this.invoice();
    const trialShortName = this.mainQuery.getSelectedTrial()?.short_name || '';
    const fileTimestamp = dayjs().format('YYYY.MM.DD-HHmmss');
    const currentTz = dayjs.tz.guess();

    const { errors, success } = await firstValueFrom(
      this.eventService.processEvent$({
        entity_id: invoice.id,
        entity_type: EntityType.INVOICE,
        payload: JSON.stringify({
          export_entity_id: invoice.id || '',
          export_type: ExportType.INVOICE_LINE_ITEMS_INTEGRATION,
          filename: `${invoice.invoice_no}_${invoice.organization.name}_${trialShortName}_Auxilius${Utils.getIntegrationSourceFileNaming(invoice.data_source_id)}_Invoice_Line_Items`,
          json_properties: {
            fileTimestamp: `${fileTimestamp} ${currentTz}`,
          },
        }),
        type: EventType.GENERATE_EXPORT,
      })
    );

    if (success) {
      this.overlayService.success(
        'Export is being generated and will download when complete. You may leave the page.'
      );
    } else {
      this.overlayService.error(errors);
    }
  }

  openMapActivitiesModal(): void {
    this.changeTab.emit('integration');
  }
}
